import { queryOptions } from '@tanstack/react-query';

import { handleGetClientSessionInfo } from '~/services/ClientServiceTS';

/** Observer / idle refresh window for `useSessionPlQuery`. */
export const SESSION_PL_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Middleware navigation window. Long enough that account-root → permission
 * redirect (two `startNavigation`s, different AbortSignals) reuses one
 * `session.pl`, short enough that later navigations still refresh cookies /
 * CSRF / idle timer.
 */
export const SESSION_PL_MIDDLEWARE_STALE_TIME_MS = 5_000;

export function getSessionPlQueryKey({
  accountId,
  accountType,
}: {
  accountId?: string;
  accountType?: string;
}) {
  return ['session.pl', accountId, accountType] as const;
}

function stripCsrfToken<T extends Awaited<ReturnType<typeof handleGetClientSessionInfo>>>(
  data: T,
): T {
  if (!data?.login?.csrf_token) {
    return data;
  }
  const copyData = { ...data, login: { ...data.login } };
  delete copyData.login.csrf_token;
  return copyData;
}

/**
 * Shared session.pl query options for middleware and `useSessionPlQuery`.
 * Middleware overrides `staleTime` to `SESSION_PL_MIDDLEWARE_STALE_TIME_MS`.
 */
export function getSessionPlQueryOptions({
  accountId,
  accountType,
}: {
  accountId?: string;
  accountType?: string;
} = {}) {
  const sessionAccountType =
    accountType === 'client' || accountType === 'partner' ? accountType : undefined;

  return queryOptions({
    queryKey: getSessionPlQueryKey({ accountId, accountType: sessionAccountType }),
    queryFn: async () => {
      const data = await handleGetClientSessionInfo(null, {
        accountId,
        accountType: sessionAccountType,
      });
      return stripCsrfToken(data);
    },
    staleTime: SESSION_PL_STALE_TIME_MS,
    retry: 0,
  });
}
